/**
 * Dexie(IndexedDB) 数据库定义。
 *
 * 表：conversations / messages / presets / providers / memories。
 * 索引设计满足后续模块的查询需求：
 * - messages 以 `[conversationId+createdAt]` 复合索引支撑「按会话 + 时间倒序分页」；
 * - conversations 以 `updatedAt` 支撑会话列表排序；
 * - presets 以 `name` / `source` 支撑角色卡检索与社区卡筛选；
 * - providers 以 `kind` / `enabled` 支撑提供商列表过滤。
 */

import Dexie, { type Table } from 'dexie';

import type { LedgerEntry } from './api';
import type {
  AutomationEvent,
  Conversation,
  ChatMessage,
  MemoryEntry,
  Moment,
  MomentSyncConfig,
  MomentReceipt,
  Preset,
  ProviderConfig,
} from './types';
// 图库实体由 gallery 特性定义；仅类型引用（type-only），不产生运行时循环依赖。
import type { GalleryImage } from '../features/gallery/galleryRepo';
import type { PersonalSkin, SkinCacheEntry } from '../features/appearance/types';
import type { MomentInteraction } from '../features/moments/interactionRepo';

export const DB_NAME = 'zxtalk';
export const DB_VERSION = 7;

/** 只读的余额流水备份快照（恢复时写入，不参与钱包/账本写接口）。 */
export interface BackupLedgerEntry {
  /** `${backupId}:${ledgerEntry.id}`，同一备份重复导入保持幂等。 */
  id: string;
  userId: string;
  backupId: string;
  capturedAt: number;
  entry: LedgerEntry;
}

export interface DatabaseSnapshot {
  conversations: Conversation[];
  messages: ChatMessage[];
  presets: Preset[];
  providers: ProviderConfig[];
  memories: MemoryEntry[];
}

export class ZxTalkDatabase extends Dexie {
  conversations!: Table<Conversation, string>;
  messages!: Table<ChatMessage, string>;
  presets!: Table<Preset, string>;
  providers!: Table<ProviderConfig, string>;
  memories!: Table<MemoryEntry, string>;
  gallery!: Table<GalleryImage, string>;
  backupLedgerEntries!: Table<BackupLedgerEntry, string>;
  appearancePersonalSkins!: Table<PersonalSkin, string>;
  appearanceSkinCache!: Table<SkinCacheEntry, string>;
  moments!: Table<Moment, string>;
  automationEvents!: Table<AutomationEvent, string>;
  momentSyncConfigs!: Table<MomentSyncConfig, string>;
  momentReceipts!: Table<MomentReceipt, string>;
  momentInteractions!: Table<MomentInteraction, string>;

  constructor(name: string = DB_NAME) {
    super(name);

    this.version(1).stores({
      conversations: 'id, updatedAt, createdAt, presetId, providerId',
      messages: 'id, conversationId, createdAt, [conversationId+createdAt], status',
      presets: 'id, name, source',
      providers: 'id, kind, enabled',
      memories: 'id, updatedAt, createdAt, importance, *tags',
    });

    // T11b 追加：图库表（追加式改动，未改动 v1 既有表结构）。
    // IndexedDB 不能以 boolean 作索引键，favorite 索引仅作声明；过滤在仓储内完成。
    this.version(2).stores({
      gallery: 'id, createdAt, favorite',
    });

    // 加密备份恢复：余额流水只读快照归档（按账号 + 备份时间检索）。
    this.version(3).stores({
      backupLedgerEntries: 'id, userId, capturedAt, [userId+capturedAt]',
    });

    this.version(4).stores({
      appearancePersonalSkins: 'id, updatedAt, createdAt',
      appearanceSkinCache: 'id, cachedAt',
    });

    this.version(5).stores({
      moments: 'id, presetId, createdAt, [presetId+createdAt]',
      automationEvents: 'id, presetId, dueAt, status, [status+dueAt]',
    });

    this.version(6).stores({
      moments: 'id, presetId, createdAt, [presetId+createdAt], userId',
      momentSyncConfigs: 'id, userId',
      momentReceipts: 'id, userId, postId',
    });
    this.version(7).stores({
      momentInteractions: 'id, userId, postId, [userId+presetId], updatedAt',
    });
  }
}

let defaultDatabase: ZxTalkDatabase | undefined;

/** 获取应用默认数据库（惰性创建，避免模块加载时就要求 IndexedDB 存在）。 */
export function getDb(): ZxTalkDatabase {
  defaultDatabase ??= new ZxTalkDatabase();
  return defaultDatabase;
}

/**
 * 替换默认数据库实例。
 * 主要用于测试隔离（每个用例一个全新 DB）与多账号/多空间场景。
 */
export function setDb(db: ZxTalkDatabase | undefined): void {
  defaultDatabase = db;
}

/** 创建独立命名的数据库实例。 */
export function createDatabase(name: string = DB_NAME): ZxTalkDatabase {
  return new ZxTalkDatabase(name);
}

/** 关闭并删除数据库（测试清理用）。 */
export async function deleteDatabase(name: string = DB_NAME): Promise<void> {
  await Dexie.delete(name);
}

/**
 * 旧版备份快照包含的表（导入/导出遍历用）。
 *
 * T13 追加的 memories 表由 memoryRepo 独立管理，未纳入旧快照集合，
 * 以保证既有备份格式（DatabaseSnapshot / 	ransfer.ts）完全向后兼容。
 */
export const TABLE_NAMES = ['conversations', 'messages', 'presets', 'providers'] as const;
export type TableName = (typeof TABLE_NAMES)[number];
